import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Filter, Search, Pencil, FileText } from 'lucide-react'; // Added FileText
import { supabase } from '../../../lib/supabase'; // Import supabase client
import { TableColumn, ColumnWidth } from '../../../components/common/EnhancedTable/types';
import './EnhancedUnderLeases.css';
import ExpandableText from '../../../components/common/ExpandableText';
import { supabaseUnderLeaseService } from '../../../services/supabase/underleases';
import type { UnderLease } from '../../../types/underlease';
import { formatDate } from '../../../utils/dateUtils';
import { formatTextWithLinks } from '../../../utils/formatters';
import { usePermissions } from '../../../hooks/usePermissions';
import AddUnderLeaseModal from './components/AddUnderLeaseModal';
import EditUnderLeaseModal from './components/EditUnderLeaseModal';
import ViewUnderLeaseLotsModal from './components/ViewUnderLeaseLotsModal'; // Import the new modal

// Define the sort field type
type UnderLeaseSortField = 'underlease_lease' | 'underlease_holder' | 'underlease_start' | 'underlease_end' | 'underlease_use';

// Define the sort configuration type
interface SortConfig {
  field: UnderLeaseSortField;
  direction: 'asc' | 'desc';
}

// Define the filter type
interface UnderLeaseFilter {
  startDateFrom: string | null;
  startDateTo: string | null;
  endDateFrom: string | null;
  endDateTo: string | null;
  useType: string | null;
}

/**
 * Pagination controls component
 */
function PaginationControls({
  pagination,
  className = ''
}: {
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    pageSizeOptions: number[];
  };
  className?: string;
}) {
  const { currentPage, pageSize, totalCount, onPageChange, onPageSizeChange, pageSizeOptions = [25, 50, 100, 200] } = pagination;
  
  // Calculate pagination info
  const totalPages = Math.ceil(totalCount / pageSize);
  const startRow = (currentPage - 1) * pageSize + 1;
  const endRow = Math.min(currentPage * pageSize, totalCount);
  
  // Generate page numbers with ellipsis for large page ranges
  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    // First page + ellipsis if needed
    if (startPage > 1) {
      pageNumbers.push(
        <button 
          key="1" 
          className="pagination-button"
          onClick={() => onPageChange(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pageNumbers.push(<span key="ellipsis1" className="px-1">...</span>);
      }
    }
    
    // Visible page numbers
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          className={`pagination-button ${currentPage === i ? 'active' : ''}`}
          onClick={() => onPageChange(i)}
        >
          {i}
        </button>
      );
    }
    
    // Ellipsis + last page if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push(<span key="ellipsis2" className="px-1">...</span>);
      }
      pageNumbers.push(
        <button
          key={totalPages}
          className="pagination-button"
          onClick={() => onPageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }
    
    return pageNumbers;
  };
  
  return (
    <div className={`pagination-controls ${className}`}>
      {/* Information about displayed entries */}
      <div className="text-sm text-gray-600">
        {totalCount === 0 ? (
          'No entries to display'
        ) : (
          `Showing ${startRow} to ${endRow} of ${totalCount} entries`
        )}
      </div>
      
      {/* Pagination controls */}
      <div className="flex items-center space-x-2">
        {/* Page size selector */}
        <select 
          className="border rounded p-1 text-sm"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {pageSizeOptions.map(size => (
            <option key={size} value={size}>{size} per page</option>
          ))}
        </select>
        
        {/* Previous page button */}
        <button 
          className={`pagination-button ${currentPage === 1 ? 'disabled' : ''}`}
          onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        
        {/* Page number buttons */}
        <div className="flex space-x-1">
          {renderPageNumbers()}
        </div>
        
        {/* Next page button */}
        <button 
          className={`pagination-button ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}
          onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          Next
        </button>
      </div>
    </div>
  );
}

/**
 * EnhancedUnderLeases component
 * 
 * This component displays a table of under-leases with filtering, sorting, and pagination.
 * It uses a custom data processing approach to prevent duplicate records when sorting.
 */
export default function EnhancedUnderLeases() {
  const queryClient = useQueryClient();
  const { canEditLeases } = usePermissions();
  
  // State for filters, sorting, and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<UnderLeaseFilter>({
    startDateFrom: null,
    startDateTo: null,
    endDateFrom: null,
    endDateTo: null,
    useType: null
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'underlease_lease',
    direction: 'asc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // State for modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUnderLease, setSelectedUnderLease] = useState<UnderLease | null>(null);
  const [isViewLotsModalOpen, setIsViewLotsModalOpen] = useState(false); // State for new modal
  const [selectedSubleaseIdForLots, setSelectedSubleaseIdForLots] = useState<string | null>(null); // State for parent sublease_id

  // State for the processed data
  const [rawData, setRawData] = useState<UnderLease[]>([]);
  const [allLeaseLots, setAllLeaseLots] = useState<{ leaselot_lease: number; leaselot_lot: string }[]>([]); // New state for lease_lots
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Refs for horizontal scrolling
  const headerWrapperRef = useRef<HTMLDivElement>(null);
  const bodyContainerRef = useRef<HTMLDivElement>(null);
  const topScrollbarRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Fetch data directly without using React Query
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch UnderLeases
        const underLeaseData = await supabaseUnderLeaseService.getAll();
        console.log('Raw underLeaseData fetched:', underLeaseData.length, 'records');
        setRawData(underLeaseData);

        // Fetch all lease_lots records
        const { data: leaseLotsData, error: leaseLotsError } = await supabase
          .from('lease_lots')
          .select('leaselot_lease, leaselot_lot');
        
        if (leaseLotsError) {
          console.error('Error fetching allLeaseLots:', leaseLotsError);
          // Decide if this is a critical error or if the page can function without it
          // For now, we'll set it and let the accessor handle missing data
          setAllLeaseLots([]);
        } else if (leaseLotsData) {
          setAllLeaseLots(leaseLotsData as { leaselot_lease: number; leaselot_lot: string }[]);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Extract unique use types for filter dropdowns
  const useTypes = useMemo(() => {
    const useTypesSet = new Set<string>();

    rawData.forEach(lease => {
      if (lease.underlease_use) useTypesSet.add(lease.underlease_use);
    });

    return Array.from(useTypesSet).sort();
  }, [rawData]);

  // Create a map of sublease_id to an array of lot_ids
  const leaseLotsMap = useMemo(() => {
    const map = new Map<number, string[]>();
    allLeaseLots.forEach(ll => {
      const lots = map.get(ll.leaselot_lease) || [];
      if (!lots.includes(ll.leaselot_lot)) { // Ensure unique lot_ids per lease
        lots.push(ll.leaselot_lot);
      }
      map.set(ll.leaselot_lease, lots);
    });
    return map;
  }, [allLeaseLots]);

  // Apply filters
  const filteredUnderleases = useMemo(() => {
    if (!rawData) return [];
    
    let filtered = [...rawData];
    
    // Apply search
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(lease => {
        const matchesSearch = 
          lease.underlease_lease.toString().includes(searchLower) ||
          lease.underlease_holder.toString().includes(searchLower) ||
          lease.underlease_use.toLowerCase().includes(searchLower);
        
        return matchesSearch;
      });
    }

    // Apply filters
    if (filters.useType) {
      filtered = filtered.filter(lease => lease.underlease_use === filters.useType);
    }

    if (filters.startDateFrom) {
      filtered = filtered.filter(lease => new Date(lease.underlease_start) >= new Date(filters.startDateFrom!));
    }

    if (filters.startDateTo) {
      filtered = filtered.filter(lease => new Date(lease.underlease_start) <= new Date(filters.startDateTo!));
    }

    if (filters.endDateFrom) {
      filtered = filtered.filter(lease => new Date(lease.underlease_end) >= new Date(filters.endDateFrom!));
    }

    if (filters.endDateTo) {
      filtered = filtered.filter(lease => new Date(lease.underlease_end) <= new Date(filters.endDateTo!));
    }

    console.log('Filtered data:', filtered.length, 'records');
    
    return filtered;
  }, [rawData, searchQuery, filters]);

  // Apply sorting
  const sortedUnderleases = useMemo(() => {
    if (!filteredUnderleases) return [];
    
    console.log('Sorting underleases with config:', sortConfig);
    
    return [...filteredUnderleases].sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      switch (sortConfig.field) {
        case 'underlease_lease':
          return direction * (a.underlease_lease - b.underlease_lease);
        case 'underlease_holder':
          return direction * (a.underlease_holder - b.underlease_holder);
        case 'underlease_start':
          return direction * a.underlease_start.localeCompare(b.underlease_start);
        case 'underlease_end':
          return direction * a.underlease_end.localeCompare(b.underlease_end);
        case 'underlease_use':
          return direction * a.underlease_use.localeCompare(b.underlease_use);
        default:
          return 0;
      }
    });
  }, [filteredUnderleases, sortConfig]);

  // Apply pagination
  const paginatedUnderleases = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    
    console.log('Paginating underleases:', {
      total: sortedUnderleases.length,
      page: currentPage,
      pageSize,
      startIndex,
      endIndex
    });
    
    return sortedUnderleases.slice(startIndex, endIndex);
  }, [sortedUnderleases, currentPage, pageSize]);

  // Synchronize horizontal scrolling between header, body, and top scrollbar
  useEffect(() => {
    const headerWrapper = headerWrapperRef.current;
    const bodyContainer = bodyContainerRef.current;
    const topScrollbar = topScrollbarRef.current;

    if (!headerWrapper || !bodyContainer || !topScrollbar) return;

    const handleBodyScroll = () => {
      // Sync header and top scrollbar with body
      headerWrapper.scrollLeft = bodyContainer.scrollLeft;
      topScrollbar.scrollLeft = bodyContainer.scrollLeft;
      
      // Check scroll positions for indicators
      const hasMoreRight = bodyContainer.scrollWidth > bodyContainer.clientWidth &&
                          bodyContainer.scrollLeft < (bodyContainer.scrollWidth - bodyContainer.clientWidth - 5);
      setCanScrollRight(hasMoreRight);
      setCanScrollLeft(bodyContainer.scrollLeft > 5);
    };

    const handleTopScroll = () => {
      // Sync body and header with top scrollbar
      bodyContainer.scrollLeft = topScrollbar.scrollLeft;
      headerWrapper.scrollLeft = topScrollbar.scrollLeft;
    };

    // Initial check
    handleBodyScroll();

    bodyContainer.addEventListener('scroll', handleBodyScroll);
    topScrollbar.addEventListener('scroll', handleTopScroll);
    
    // Force a reflow to ensure the scrollbar is visible and properly sized
    setTimeout(() => {
      if (topScrollbar && bodyContainer) {
        // Force a reflow by accessing offsetWidth
        const width = bodyContainer.scrollWidth;
        topScrollbar.style.width = width + 'px';
        topScrollbar.style.width = '100%';
        
        // Trigger a scroll event to update indicators
        const event = new Event('scroll');
        bodyContainer.dispatchEvent(event);
      }
    }, 100);
    
    return () => {
      bodyContainer.removeEventListener('scroll', handleBodyScroll);
      topScrollbar.removeEventListener('scroll', handleTopScroll);
    };
  }, [paginatedUnderleases]); // Re-run when data changes

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<UnderLeaseFilter>) => {
    setFilters(current => ({ ...current, ...newFilters }));
  };

  // Handle edit under-lease
  const handleEditUnderLease = (underlease: UnderLease) => {
    setSelectedUnderLease(underlease);
    setIsEditModalOpen(true);
  };

  // Handle modal success
  const handleSuccess = async () => {
    try {
      setLoading(true);
      const data = await supabaseUnderLeaseService.getAll();
      console.log('Data refreshed after modal action:', data.length, 'records');
      setRawData(data);
      setLoading(false);
    } catch (err) {
      console.error('Error refreshing data:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      setLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (field: UnderLeaseSortField) => {
    setSortConfig(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Calculate pagination configuration
  const paginationConfig = {
    currentPage,
    pageSize,
    totalCount: sortedUnderleases.length,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [25, 50, 100, 200]
  };

  // Get sort indicator
  const getSortIndicator = (field: string) => {
    if (sortConfig.field !== field) return null;
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Create a map to track unique records by composite key
  const uniqueRecordsMap = useMemo(() => {
    const map = new Map<string, UnderLease>();
    
    paginatedUnderleases.forEach(lease => {
      const compositeKey = `${lease.underlease_lease}_${lease.underlease_holder}_${lease.underlease_start}`;
      if (!map.has(compositeKey)) {
        map.set(compositeKey, lease);
      }
    });
    
    return map;
  }, [paginatedUnderleases]);

  // Get unique records from the map
  const uniqueRecords = useMemo(() => {
    return Array.from(uniqueRecordsMap.values());
  }, [uniqueRecordsMap]);

  // Define column widths
  const columnWidths = {
    edit: '80px',
    sublease_holder: '180px',
    underlease_holder_name: '180px',
    underlease_lease: '120px',
    underlease_holder: '120px',
    underlease_start: '150px',
    underlease_end: '150px',
    underlease_use: '300px',
    lots_list_icon: '80px', // For the icon button
    associated_lots_text: '250px', // For the concatenated text list
    // associated_lots: '120px' // This was the old one, replaced by the two above
  };

  const handleViewAssociatedLots = (underlease: UnderLease) => {
    if (underlease.underlease_lease) { // underlease_lease is the parent sublease_id
      setSelectedSubleaseIdForLots(underlease.underlease_lease.toString());
      // Assuming underlease_id or similar can be used as an identifier for the modal title
      // If UnderLease type doesn't have a unique ID for itself, we can pass undefined
      // For now, let's assume UnderLease type has an 'id' or similar unique field for the underlease itself
      // If not, the modal title will just use the subleaseId.
      // const identifier = underlease.id || `UnderLease related to SubLease ${underlease.underlease_lease}`;
      setIsViewLotsModalOpen(true);
    } else {
      console.warn('Parent sublease ID (underlease_lease) is missing for this underlease.');
      // Optionally, show an error to the user
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading under-lease data: {error.message}
      </div>
    );
  }

  return (
    <div className="enhanced-underleases-container p-4">
      <div className="enhanced-underleases-header">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Under-Leases</h1>
            
            {/* Add button - only visible to users with edit permissions */}
            {canEditLeases && (
              <button 
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Under-Lease
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Search input */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search under-leases..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            
            {/* Filter button */}
            <button
              className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              title="Toggle filters"
            >
              <Filter className="h-5 w-5 text-gray-500" />
              <span className="hidden sm:inline">Filters</span>
            </button>
            
            <div className="text-base font-bold text-gray-700">
              Total Under-Leases: {rawData.length}
            </div>
          </div>

          {/* Filter panel */}
          {isFilterOpen && (
            <div className="filter-panel">
              <div className="filter-panel-header">
                <h3 className="font-medium text-gray-700">Filters</h3>
                <button
                  onClick={() => setFilters({
                    startDateFrom: null,
                    startDateTo: null,
                    endDateFrom: null,
                    endDateTo: null,
                    useType: null
                  })}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              </div>
              <div className="filter-panel-body">
                {/* Use type filter */}
                <div className="filter-group">
                  <label htmlFor="use-type-filter">Use Type</label>
                  <select
                    id="use-type-filter"
                    value={filters.useType || ''}
                    onChange={(e) => handleFilterChange({ useType: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All use types</option>
                    {useTypes.map(useType => (
                      <option key={useType} value={useType}>{useType}</option>
                    ))}
                  </select>
                </div>
                
                {/* Start date range filter */}
                <div className="filter-group">
                  <label htmlFor="start-date-from">Start Date From</label>
                  <input
                    type="date"
                    id="start-date-from"
                    value={filters.startDateFrom || ''}
                    onChange={(e) => handleFilterChange({ startDateFrom: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
                
                <div className="filter-group">
                  <label htmlFor="start-date-to">Start Date To</label>
                  <input
                    type="date"
                    id="start-date-to"
                    value={filters.startDateTo || ''}
                    onChange={(e) => handleFilterChange({ startDateTo: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
                
                {/* End date range filter */}
                <div className="filter-group">
                  <label htmlFor="end-date-from">End Date From</label>
                  <input
                    type="date"
                    id="end-date-from"
                    value={filters.endDateFrom || ''}
                    onChange={(e) => handleFilterChange({ endDateFrom: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
                
                <div className="filter-group">
                  <label htmlFor="end-date-to">End Date To</label>
                  <input
                    type="date"
                    id="end-date-to"
                    value={filters.endDateTo || ''}
                    onChange={(e) => handleFilterChange({ endDateTo: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Enhanced table with horizontal scrolling */}
      <div className="enhanced-table-container">
        {/* Pagination controls at the top */}
        <PaginationControls 
          pagination={paginationConfig}
          className="px-4 py-2 border-b border-gray-200 mb-2"
        />
        
        {/* Table container with direct scrolling */}
        <div className="table-with-scrollbar-container">
          {/* Direct table scrollbar */}
          <div className="table-scrollbar-container">
            <div 
              ref={topScrollbarRef}
              className="table-horizontal-scrollbar"
              onScroll={(e) => {
                if (bodyContainerRef.current) {
                  bodyContainerRef.current.scrollLeft = e.currentTarget.scrollLeft;
                }
                if (headerWrapperRef.current) {
                  headerWrapperRef.current.scrollLeft = e.currentTarget.scrollLeft;
                }
              }}
            >
              <div style={{ width: '1200px', height: '1px' }}></div>
            </div>
          </div>
          
          {/* Single table with fixed layout */}
          <div 
            ref={bodyContainerRef}
            className="enhanced-table-body-container enhanced-table-force-scroll"
            style={{ 
              maxHeight: 'calc(100vh - 20rem)',
              overflowX: 'auto',
              overflowY: 'auto'
            }}
            onScroll={(e) => {
              if (topScrollbarRef.current) {
                topScrollbarRef.current.scrollLeft = e.currentTarget.scrollLeft;
              }
              if (headerWrapperRef.current) {
                headerWrapperRef.current.scrollLeft = e.currentTarget.scrollLeft;
              }
            }}
          >
            {/* Left scroll indicator */}
            <div className={`enhanced-table-scroll-left-indicator ${canScrollLeft ? 'visible' : 'hidden'}`} />
            
            {/* Right scroll indicator */}
            <div className={`enhanced-table-scroll-right-indicator ${canScrollRight ? 'visible' : 'hidden'}`} />
            
            <table className="enhanced-table" style={{ width: '1200px', tableLayout: 'fixed' }}>
              <colgroup>
                {canEditLeases && <col style={{ width: columnWidths.edit }} />}
                <col style={{ width: columnWidths.sublease_holder }} />
                <col style={{ width: columnWidths.underlease_holder_name }} />
                <col style={{ width: columnWidths.lots_list_icon }} />
                <col style={{ width: columnWidths.associated_lots_text }} />
                <col style={{ width: columnWidths.underlease_start }} />
                <col style={{ width: columnWidths.underlease_end }} />
                <col style={{ width: columnWidths.underlease_use }} />
                <col style={{ width: columnWidths.underlease_lease }} />
                <col style={{ width: columnWidths.underlease_holder }} />
              </colgroup>
              <thead>
                <tr>
                  {canEditLeases && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Edit
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SUB-LEASE HOLDER
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    UNDERLEASE HOLDER
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    LOTS LIST
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ASSOCIATED LOTS
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('underlease_start')}
                  >
                    Start Date
                    {getSortIndicator('underlease_start')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('underlease_end')}
                  >
                    End Date
                    {getSortIndicator('underlease_end')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('underlease_use')}
                  >
                    Use Type
                    {getSortIndicator('underlease_use')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('underlease_lease')}
                  >
                    Sublease ID
                    {getSortIndicator('underlease_lease')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('underlease_holder')}
                  >
                    Holder ID
                    {getSortIndicator('underlease_holder')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {uniqueRecords.map((underlease, index) => {
                  const associatedLotIds = leaseLotsMap.get(underlease.underlease_lease) || [];
                  const associatedLotsString = associatedLotIds.join(', ');

                  return (
                    <tr key={`${underlease.underlease_lease}_${underlease.underlease_holder}_${underlease.underlease_start}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {canEditLeases && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleEditUnderLease(underlease)}
                            className="w-8 h-8 flex items-center justify-center text-primary hover:text-primary-dark bg-blue-50 hover:bg-blue-100 rounded-lg"
                            title="Edit Under-Lease"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 truncate" title={underlease.sublease_holder_name || 'Unknown'}>
                          {underlease.sublease_holder_name || 'Unknown'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 truncate" title={underlease.underlease_holder_name || 'Unknown'}>
                          {underlease.underlease_holder_name || 'Unknown'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleViewAssociatedLots(underlease)}
                          className="w-8 h-8 flex items-center justify-center text-primary hover:text-primary-dark bg-blue-50 hover:bg-blue-100 rounded-lg"
                          title="View Associated Lots"
                        >
                          <FileText className="h-4 w-4" />
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 truncate" title={associatedLotsString}>
                          {associatedLotsString || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 truncate" title={formatDate(underlease.underlease_start)}>
                          {formatDate(underlease.underlease_start)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 truncate" title={formatDate(underlease.underlease_end)}>
                          {formatDate(underlease.underlease_end)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 truncate" title={underlease.underlease_use || 'Not specified'}>
                          <ExpandableText 
                            text={underlease.underlease_use || 'Not specified'} 
                            maxLength={50} 
                            className="text-sm"
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 truncate" title={underlease.underlease_lease.toString()}>
                          {underlease.underlease_lease}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 truncate" title={underlease.underlease_holder.toString()}>
                          {underlease.underlease_holder}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* Horizontal scroll indicator */}
            <div className="enhanced-table-scroll-indicator" />
          </div>
        </div>
        
        {/* Pagination controls at the bottom */}
        <PaginationControls 
          pagination={paginationConfig}
          className="px-4 py-2 border-t border-gray-200"
        />
      </div>
      
      {/* Add and Edit Modals */}
      <AddUnderLeaseModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleSuccess}
      />
      
      <EditUnderLeaseModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={handleSuccess}
        underlease={selectedUnderLease}
      />

      {selectedSubleaseIdForLots && (
        <ViewUnderLeaseLotsModal
          isOpen={isViewLotsModalOpen}
          onClose={() => {
            setIsViewLotsModalOpen(false);
            setSelectedSubleaseIdForLots(null);
          }}
          subleaseId={selectedSubleaseIdForLots}
          // Pass a unique identifier for the underlease if available, e.g., underlease.id
          // underLeaseIdentifier={selectedUnderLease?.id?.toString()} // Example if underlease has an 'id'
        />
      )}
    </div>
  );
}
